import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Check, ArrowRight, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { Github as SiGithub } from "lucide-react";
import { login } from "../../services/Service";
import { toast } from "react-toastify";
import "../Signup/Signup.css";
// Reuse existing styles

const Login = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: "",
        password: "",
    });

    const [status, setStatus] = useState("Sign In");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Clear session storage to ensure no leakage from previous users
        sessionStorage.clear();
        // Also ensure no old user data is lurking if we navigate directly to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
    }, []);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    const handleFocus = (e) => {
        e.target.style.borderColor = "var(--signup-teal)";
    };

    const handleBlur = (e) => {
        if (e.target.value.trim() !== "") {
            e.target.style.borderColor = "var(--signup-teal)";
        } else {
            e.target.style.borderColor = "#E5E7EB";
        }
    };

    const getErrorMessage = (error) => {
        // RTK Query unwrapped errors have status and data directly on the error object
        const status = error.status || error.response?.status;
        const data = error.data || error.response?.data;
        const serverMessage = data?.message || data?.msg;
        const serverError = data?.error;

        if (!status && !error.response) {
            if (error.message === "Network Error") {
                return "Network error. Please check your internet connection and try again.";
            }
            if (error.code === "ECONNABORTED") {
                return "Request timeout. The server took too long to respond. Please try again.";
            }
            return error.message || "Network error. Please check your connection and try again.";
        }

        if (status === 400) {
            return serverMessage || serverError || "Invalid input. Please check your details and try again.";
        }

        if (status === 401) {
            return serverMessage || serverError || "Invalid email or password. Please try again.";
        }

        if (status === 409) {
            return serverMessage || serverError || "This email is already registered. Please sign in or use a different email.";
        }

        if (status === 500) {
            if (serverError?.includes("salt") || serverMessage?.includes("decrypt")) {
                return "Server configuration error. Please contact support or try again later.";
            }
            return "Server error. Please try again later or contact support.";
        }

        return serverMessage || serverError || "Operation failed. Please try again.";
    };

    const validateForm = () => {
        if (!formData.email?.trim()) {
            toast.warning("Email is required");
            return false;
        }
        if (!formData.password?.trim()) {
            toast.warning("Password is required");
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast.warning("Please enter a valid email address");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            setStatus("Signing in...");
            const response = await login(formData);
            console.log("Login successful:", response);
            toast.success("Signed in successfully!");

            // Token storage is handled inside the login service

            setStatus(
                <span>
                    Signed in! <Check size={16} />
                </span>
            );

            setTimeout(() => {
                navigate("/tasks");
                setIsLoading(false);
            }, 800);
        } catch (err) {
            setIsLoading(false);
            const errorMessage = getErrorMessage(err);
            toast.error(errorMessage);
            setStatus("Sign In");
            console.error("Authentication Error:", err);
        }
    };

    return (
        <div className="signup-page-wrapper">
            <div className="signup-bg-animation">
                <div className="bg-shape shape-1"></div>
                <div className="bg-shape shape-2"></div>
                <div className="bg-shape shape-3"></div>
                <div className="bg-shape shape-4"></div>
            </div>

            <div className="particles">
                {[...Array(9)].map((_, i) => (
                    <div key={i} className="particle"></div>
                ))}
            </div>

            <div className="signup-container">
                <div className="left-panel">
                    <div className="logo-container">
                        <svg className="product-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                            <path d="M 20 35 L 45 35" stroke="#1E40AF" strokeWidth="4" strokeLinecap="round"></path>
                            <path d="M 20 50 L 55 50" stroke="#14B8A6" strokeWidth="4" strokeLinecap="round"></path>
                            <path d="M 20 65 L 65 65" stroke="#F59E0B" strokeWidth="4" strokeLinecap="round"></path>
                            <path d="M 45 35 L 55 40 L 45 45 Z" fill="#1E40AF"></path>
                            <path d="M 55 50 L 65 55 L 55 60 Z" fill="#14B8A6"></path>
                            <path d="M 65 65 L 75 70 L 65 75 Z" fill="#F59E0B"></path>
                            <circle cx="80" cy="50" r="8" fill="#1E40AF"></circle>
                        </svg>
                        <span className="logo-text">SequFlow</span>
                    </div>

                    <div className="hero-content">
                        <h1 className="hero-title">Welcome Back</h1>
                        <p className="hero-subtitle">
                            Sign in to continue managing your workflows, tracking progress, and delivering exceptional results with SequFlow.
                        </p>
                    </div>

                    <div className="signup-features">
                        <div className="feature">
                            <div className="feature-icon"><Check size={14} /></div>
                            <span>Secure Login</span>
                        </div>
                        <div className="feature">
                            <div className="feature-icon"><Check size={14} /></div>
                            <span>Real-time Sync</span>
                        </div>
                        <div className="feature">
                            <div className="feature-icon"><Check size={14} /></div>
                            <span>Team Access</span>
                        </div>
                    </div>

                    <div className="flow-visualization">
                        <div className="flow-viz-title">Workflow Progress</div>
                        <div className="flow-lines">
                            <div className="flow-item">
                                <div className="flow-bar"></div>
                                <div className="flow-arrow"><ArrowRight size={14} /></div>
                            </div>
                            <div className="flow-item">
                                <div className="flow-bar"></div>
                                <div className="flow-arrow"><ArrowRight size={14} /></div>
                            </div>
                            <div className="flow-item">
                                <div className="flow-bar"></div>
                                <div className="flow-arrow"><Check size={14} /></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="right-panel">
                    <div className="form-container">
                        <div className="form-header">
                            <h2 className="form-title">Sign in</h2>
                            <p className="form-subtitle">
                                Don't have an account? <Link to="/signup" className="signup-link">Create one</Link>.
                            </p>
                        </div>

                        <div className="social-login">
                            <button className="social-btn"><SiGoogle size={18} /> Google</button>
                            <button className="social-btn"><SiGithub size={18} /> GitHub</button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="email">Email address</label>
                                <div className="input-wrapper">
                                    <Mail className="input-icon" size={18} />
                                    <input
                                        type="email"
                                        id="email"
                                        placeholder="you@company.com"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <div className="password-header">
                                    <label htmlFor="password">Password</label>
                                </div>
                                <div className="input-wrapper">
                                    <Lock className="input-icon" size={18} />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        placeholder="Enter your password"
                                        required
                                        value={formData.password}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex="-1"
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>

                                <div className="forgot-password-section">
                                    <Link to="/forgot" className="forgot-password-link">
                                        Forgot your password?
                                    </Link>
                                </div>
                            </div>

                            <button type="submit" className="submit-btn" disabled={isLoading}>
                                {status}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
